import React from 'react';
import Select, { components } from 'react-select';
import axiosInstance, { weburl } from '../../axiosInstance';

const getDriverImage = (driver) => {
  const image = driver?.profile || driver?.ProfileFile || driver?.profileImage || driver?.avatar || '';
  if (!image || typeof image !== 'string') return '';
  if (image.startsWith('http') || image.startsWith('blob:') || image.startsWith('data:')) return image;
  const cleanImage = image.replace(/\\/g, '/').replace(/^public\//, '');
  const baseUrl = axiosInstance.defaults.baseURL || weburl;
  return `${baseUrl.replace(/\/$/, '')}/${cleanImage.replace(/^\//, '')}`;
};

const getDriverStatus = (driver) => {
  if (driver?.onDuty) return { label: 'On Duty', bg: '#dbeafe', color: '#1d4ed8' };
  if (Number(driver?.online) === 1) return { label: 'Online', bg: '#dcfce7', color: '#047857' };
  return { label: 'Offline', bg: '#fee2e2', color: '#b91c1c' };
};

export const DriverAvatar = ({ driver, size = 34 }) => {
  const [imageError, setImageError] = React.useState(false);
  const image = getDriverImage(driver);
  if (image && !imageError) {
    return (
      <img
        src={image}
        alt={driver?.username || driver?.label || 'Driver'}
        className="driver-avatar-img"
        style={{ width: size, height: size }}
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div className="driver-avatar" style={{ width: size, height: size }}>
      {(driver?.username || driver?.label || 'D')[0].toUpperCase()}
    </div>
  );
};

const DriverOption = (props) => {
  const driver = props.data.driver;
  const status = getDriverStatus(driver);
  const textColor = props.isSelected ? '#fff' : '#1e293b';
  const metaColor = props.isSelected ? '#fff' : '#64748b';

  return (
    <components.Option {...props}>
      <div className="driver-select-option">
        <DriverAvatar driver={driver} size={36} />
        <div className="driver-select-copy">
          <div className="driver-select-name" style={{ color: textColor }}>{driver.username}</div>
          <div className="driver-select-meta" style={{ color: metaColor }}>
            {driver.phone || 'No phone'} <span className="driver-status-badge" style={{ background: props.isSelected ? '#fff' : status.bg, color: props.isSelected ? '#111827' : status.color }}>{status.label}</span>
            {driver.distanceKm != null ? ` ${driver.distanceKm.toFixed(2)} km` : ''}
          </div>
        </div>
      </div>
    </components.Option>
  );
};

const DriverSingleValue = (props) => {
  const driver = props.data.driver;
  const status = getDriverStatus(driver);

  return (
    <components.SingleValue {...props}>
      <div className="driver-select-option selected">
        <DriverAvatar driver={driver} size={26} />
        <span>{driver.username}</span>
        <small className="driver-status-badge" style={{ background: '#fff', color: '#111827' }}>{status.label}</small>
      </div>
    </components.SingleValue>
  );
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 10,
    borderColor: state.isFocused ? '#111827' : '#e2e8f0',
    backgroundColor: state.hasValue ? '#111827' : '#fff',
    boxShadow: state.isFocused ? '0 0 0 1px #111827' : 'none',
    ':hover': {
      borderColor: '#111827',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: '#fff',
  }),
  input: (base, state) => ({
    ...base,
    color: state.hasValue ? '#fff' : '#111827',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#64748b',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 1060,
    borderRadius: 10,
    overflow: 'hidden',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#111827' : state.isFocused ? '#f8fafc' : '#fff',
    color: state.isSelected ? '#fff' : '#111827',
    cursor: 'pointer',
    ':active': {
      backgroundColor: '#111827',
      color: '#fff',
    },
  }),
  clearIndicator: (base, state) => ({
    ...base,
    color: state.hasValue ? '#fff' : '#64748b',
    ':hover': { color: state.hasValue ? '#e5e7eb' : '#111827' },
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.hasValue ? '#fff' : '#64748b',
    ':hover': { color: state.hasValue ? '#e5e7eb' : '#111827' },
  }),
};

const AssignDriverSelect = ({ drivers, selectedDriverId, onChange }) => {
  const driverOptions = drivers.map((driver) => ({
    value: driver._id,
    label: driver.username,
    driver,
  }));
  const selectedDriverOption = driverOptions.find((option) => option.value === selectedDriverId) || null;

  return (
    <Select
      value={selectedDriverOption}
      options={driverOptions}
      onChange={(option) => onChange(option?.value || '')}
      placeholder="Select active driver..."
      isClearable
      isSearchable
      noOptionsMessage={() => 'No active drivers available'}
      components={{ Option: DriverOption, SingleValue: DriverSingleValue }}
      classNamePrefix="driver-select"
      styles={selectStyles}
    />
  );
};

export default AssignDriverSelect;
